import React from "react";

export const CaseClosePanel = ({
  caseData, closeForm, setCloseForm, closeReasons, clinics, professionals, onCloseCase,
}) => {
  const c = caseData;
  const isConsulta = c.family_intent === "consulta";
  // Regra P0: se a reserva foi confirmada mas a clínica ainda não registrou
  // a chegada (arrival ausente ou não confirmado), bloqueia o encerramento
  // manual como "internação". Força o fluxo novo (clínica registra → consultor confirma).
  const arrivalPending = !isConsulta && c.status === "reserva_confirmada" && !c.arrival?.confirmed;
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-6">
      <h2 className="font-display font-bold text-xl text-[#0B2B62] mb-1">Encerrar caso</h2>
      <p className="text-sm text-slate-500 mb-4">
        {isConsulta
          ? "Registre o desfecho — se a consulta com o profissional foi realizada — e o caso será marcado como concluído."
          : "Registre o desfecho — internação ou encerramento sem internação — e o caso será marcado como concluído."}
      </p>

      {arrivalPending && (
        <div className="mb-4 p-3 rounded-md border border-amber-300 bg-amber-50 text-sm text-amber-900" data-testid="arrival-required-notice">
          ⚠️ <b>Reserva confirmada — use o fluxo de chegada.</b> Peça à unidade para
          registrar a chegada do paciente (formulário no painel da clínica). Você
          só confirma depois — isso garante que a família receba o recibo oficial
          e os valores fiquem auditáveis.
        </div>
      )}

      {isConsulta ? (
        <div className="grid grid-cols-2 gap-2 mb-5">
          <button data-testid="outcome-consulta-realizada"
            onClick={() => setCloseForm({ ...closeForm, outcome: "consulta_realizada" })}
            className={`px-4 py-3 rounded-md border-2 text-sm font-semibold transition-colors ${closeForm.outcome === "consulta_realizada" ? "border-violet-600 bg-violet-50 text-violet-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            Consulta realizada
          </button>
          <button data-testid="outcome-nao-realizada"
            onClick={() => setCloseForm({ ...closeForm, outcome: "nao_internacao" })}
            className={`px-4 py-3 rounded-md border-2 text-sm font-semibold transition-colors ${closeForm.outcome === "nao_internacao" ? "border-orange-600 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            Não houve consulta
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2 mb-5">
          <button data-testid="outcome-internacao"
            onClick={() => !arrivalPending && setCloseForm({ ...closeForm, outcome: "internacao" })}
            disabled={arrivalPending}
            title={arrivalPending ? "Use o fluxo de chegada — clínica registra, consultor confirma" : ""}
            className={`px-4 py-3 rounded-md border-2 text-sm font-semibold transition-colors ${arrivalPending ? "border-slate-200 text-slate-400 bg-slate-50 cursor-not-allowed" : closeForm.outcome === "internacao" ? "border-green-600 bg-green-50 text-green-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            Internação realizada {arrivalPending && "(bloqueado)"}
          </button>
          <button data-testid="outcome-nao-internacao" onClick={() => setCloseForm({ ...closeForm, outcome: "nao_internacao" })}
            className={`px-4 py-3 rounded-md border-2 text-sm font-semibold transition-colors ${closeForm.outcome === "nao_internacao" ? "border-orange-600 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            Não houve internação
          </button>
        </div>
      )}

      {closeForm.outcome === "consulta_realizada" ? (
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-slate-700">Profissional</label>
            <select data-testid="close-professional" value={closeForm.professional_id || ""}
              onChange={(e) => setCloseForm({ ...closeForm, professional_id: e.target.value })}
              className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md bg-white">
              <option value="">Selecione</option>
              {(professionals || []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.display_name || p.name}
                  {p.council_type ? ` · ${p.council_type} ${p.council_number || ""} ${p.council_state || ""}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Data/hora da consulta</label>
            <input data-testid="close-consulta-datetime" type="datetime-local"
              value={closeForm.admission_datetime}
              onChange={(e) => setCloseForm({ ...closeForm, admission_datetime: e.target.value })}
              className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Modalidade</label>
            <input value="Online (telemedicina)" readOnly
              data-testid="close-consulta-modality-readonly"
              className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md bg-slate-50 text-slate-600 cursor-not-allowed"
              title="Todas as consultas do RAF Direct acontecem online pela plataforma" />
            <input type="hidden" value="online" onChange={() => setCloseForm({ ...closeForm, modality: "online" })} />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Observações (opcional)</label>
            <input data-testid="close-consulta-notes" value={closeForm.responsible}
              onChange={(e) => setCloseForm({ ...closeForm, responsible: e.target.value })}
              className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md"
              placeholder="Ex.: seguirá com retornos quinzenais" />
          </div>
        </div>
      ) : closeForm.outcome === "internacao" ? (
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-slate-700">Clínica</label>
            <select data-testid="close-clinic" value={closeForm.clinic_id} onChange={(e) => setCloseForm({ ...closeForm, clinic_id: e.target.value })}
              className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md bg-white">
              <option value="">Selecione</option>
              {clinics.map((cl) => <option key={cl.id} value={cl.id}>{cl.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Data/hora da admissão</label>
            <input data-testid="close-datetime" type="datetime-local" value={closeForm.admission_datetime}
              onChange={(e) => setCloseForm({ ...closeForm, admission_datetime: e.target.value })}
              className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Modalidade</label>
            <select data-testid="close-modality" value={closeForm.modality} onChange={(e) => setCloseForm({ ...closeForm, modality: e.target.value })}
              className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md bg-white">
              <option value="voluntária">Voluntária</option>
              <option value="involuntária">Involuntária</option>
              <option value="compulsória">Compulsória</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Responsável pela entrega</label>
            <input data-testid="close-responsible" value={closeForm.responsible} onChange={(e) => setCloseForm({ ...closeForm, responsible: e.target.value })}
              className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md" placeholder="Nome do responsável" />
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700">Motivo</label>
            <select data-testid="close-reason" value={closeForm.reason} onChange={(e) => setCloseForm({ ...closeForm, reason: e.target.value })}
              className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md bg-white">
              {closeReasons.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Observações (opcional)</label>
            <textarea data-testid="close-reason-details" rows={3} value={closeForm.reason_details}
              onChange={(e) => setCloseForm({ ...closeForm, reason_details: e.target.value })}
              className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md"
              placeholder={isConsulta ? "Detalhes adicionais sobre o encerramento sem consulta..." : "Detalhes adicionais sobre o encerramento sem internação..."} />
          </div>
          {c.clinic_id && !isConsulta && (
            <p className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-md p-2.5">
              ℹ️ A reserva atual será liberada e a vaga retornará à clínica.
            </p>
          )}
        </div>
      )}

      <button data-testid="close-case-btn" onClick={onCloseCase}
        className={`mt-5 px-5 py-2.5 text-white rounded-md font-semibold transition-colors ${
          closeForm.outcome === "consulta_realizada" ? "bg-violet-600 hover:bg-violet-700" :
          closeForm.outcome === "internacao" ? "bg-green-600 hover:bg-green-700" :
          "bg-orange-600 hover:bg-orange-700"
        }`}>
        {closeForm.outcome === "consulta_realizada" ? "Registrar consulta e encerrar"
         : closeForm.outcome === "internacao" ? "Registrar internação e encerrar"
         : isConsulta ? "Encerrar sem consulta" : "Encerrar sem internação"}
      </button>
    </section>
  );
};
